// Espelho de ApiResponse<T> do backend

export type ApiResponse<T> = {
  success: boolean
  message: string
  data?: T
  errors?: string[]
  /** Código semântico do backend (ex.: "Identity.EmailNotVerified"), usado para distinguir
   *  motivos diferentes de um mesmo status HTTP — um 403 por e-mail não verificado exige uma
   *  reação diferente de um 403 por permissão insuficiente, e o status sozinho não diz qual é qual. */
  errorCode?: string
}

// ─── Códigos de erro semânticos ─────────────────────────────────────────────

export type ApiErrorCode =
  | "NETWORK"       // Sem conexão com a internet
  | "UNREACHABLE"   // Online mas servidor inacessível (CORS / DNS / porta fechada)
  | "TIMEOUT"       // Timeout do request
  | "ABORT"         // Request cancelado manualmente
  | "SERVER"        // 5xx do backend
  | "INVALID_CREDENTIALS" // 401 em endpoints públicos (login/senha)
  | "UNAUTHORIZED"  // 401 — token expirado ou inválido
  | "FORBIDDEN"     // 403
  | "VALIDATION"    // 400/422 — erros de validação do backend
  | "NOT_FOUND"     // 404
  | "RATE_LIMITED"  // 429 — muitas tentativas na mesma janela
  | "UNKNOWN"       // Fallback

/**
 * Erro tipado lançado por http.ts.
 * Estende Error nativo para que `instanceof Error` funcione em catch blocks.
 */
export class ApiError extends Error {
  readonly code: ApiErrorCode
  readonly status?: number
  readonly errors: string[]
  /** true se o erro é potencialmente recuperável com retry */
  readonly retryable: boolean
  /** Código semântico do backend (ApiResponse.errorCode), ex.: "Identity.EmailNotVerified".
   *  Um mesmo status HTTP (403, 401...) cobre motivos bem diferentes — isto é o que permite
   *  distinguir "e-mail não verificado" de "sem permissão" sem depender do texto da mensagem. */
  readonly apiCode?: string

  constructor(
    message: string,
    code: ApiErrorCode = "UNKNOWN",
    status?: number,
    errors: string[] = [],
    apiCode?: string,
  ) {
    super(message)
    this.name = "ApiError"
    this.code = code
    this.status = status
    this.errors = errors
    this.apiCode = apiCode
    this.retryable = code === "NETWORK" || code === "UNREACHABLE" || code === "TIMEOUT" || code === "SERVER"
  }

  /** Mensagem amigável para exibir ao usuário */
  get userMessage(): string {
    if (this.apiCode === "Identity.SsoOnlyAccount") {
      return "Esta conta entra com a Microsoft. Use o botão \"Microsoft\" abaixo."
    }
    switch (this.code) {
      case "NETWORK":
        return "Sem conexão com a internet. Verifique sua rede e tente novamente."
      case "UNREACHABLE":
        return "Não foi possível conectar ao servidor. Verifique se o serviço está disponível."
      case "TIMEOUT":
        return "O servidor demorou para responder. Tente novamente em instantes."
      case "SERVER":
        return "Erro interno no servidor. Tente novamente em instantes."
      case "INVALID_CREDENTIALS":
        return this.message || "E-mail ou senha inválidos."
      case "UNAUTHORIZED":
        return "Sua sessão expirou. Faça login novamente."
      case "FORBIDDEN":
        if (this.apiCode === "Identity.EmailNotVerified") {
          return "Seu e-mail ainda não foi verificado."
        }
        if (this.apiCode === "Identity.AccountInactive") {
          return "Sua conta está inativa. Entre em contato com o suporte."
        }
        return "Você não tem permissão para esta ação."
      case "NOT_FOUND":
        return "O recurso solicitado não foi encontrado."
      case "RATE_LIMITED":
        return "Muitas tentativas em pouco tempo. Aguarde alguns instantes e tente novamente."
      case "VALIDATION":
        return this.errors.length > 0 ? this.errors.join(" ") : this.message
      default:
        return this.message || "Ocorreu um erro inesperado."
    }
  }
}

/** Helper: extrai mensagem amigável de qualquer erro */
export function friendlyMessage(err: unknown, fallback = "Ocorreu um erro inesperado."): string {
  if (err instanceof ApiError) return err.userMessage
  if (err instanceof Error) return err.message
  return fallback
}

/**
 * Detalhe técnico completo de qualquer erro, para diagnóstico (ex.: reportar o
 * bug). Diferente de `friendlyMessage`/`userMessage` (voltadas para o usuário
 * final), esta inclui tipo, mensagem crua, status/code/apiCode quando é um
 * `ApiError`, e a primeira linha do stack — o bastante para localizar a causa
 * sem expor o stack inteiro na tela.
 */
export function diagnosticMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const parts = [`status=${err.status ?? "?"}`, `code=${err.code}`]
    if (err.apiCode) parts.push(`apiCode=${err.apiCode}`)
    if (err.errors.length > 0) parts.push(`errors=${err.errors.join("; ")}`)
    return `${err.message} (${parts.join(" ")})`
  }
  if (err instanceof Error) {
    const firstStackLine = err.stack?.split("\n")[1]?.trim()
    return firstStackLine ? `${err.name}: ${err.message} — ${firstStackLine}` : `${err.name}: ${err.message}`
  }
  try {
    return `Erro não padrão: ${JSON.stringify(err)}`
  } catch {
    return `Erro não padrão: ${String(err)}`
  }
}
